import { useCallback, useEffect, useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router"
import type { PublicGuidebookPayload } from "../guidebook/types"
import { fetchPublicGuidebookBySlug } from "../guidebook/api"
import type { HouseVoice } from "../house-voice-copy"
import "./eink-review.css"

// ---------------------------------------------------------------------------
// Platform type
// ---------------------------------------------------------------------------

type Platform = "airbnb" | "booking"

// ---------------------------------------------------------------------------
// Voice-adapted copy — platform-specific
// ---------------------------------------------------------------------------

interface ReviewVoiceCopy {
  headline: string
  lead: string
  bridge: string
  footer: string
}

// Airbnb: mutual reviews (host reviews guest) → reciprocity angle
const AIRBNB_VOICE: Record<HouseVoice, ReviewVoiceCopy> = {
  house_standard: {
    headline: "Our approach to reviews",
    lead: "We leave every guest a five-star review \u2014 unless there are unreported damages to the property. We believe in honest, fair reviews on both sides.",
    bridge: "Vacation rental ratings work differently to hotels. Here\u2019s what each star actually means to Airbnb:",
    footer: "If anything could be better during your stay, we\u2019d love to hear from you.",
  },
  attentive_host: {
    headline: "Our approach to reviews",
    lead: "We leave every guest a five-star review \u2014 unless there are unreported damages to the property. We believe in honest, fair reviews on both sides.",
    bridge: "Vacation rental ratings work differently to hotels. Here\u2019s what each star actually means to Airbnb:",
    footer: "If anything could be better, please let us know \u2014 we\u2019re always happy to help.",
  },
  english_butler: {
    headline: "Our approach to reviews",
    lead: "Every guest receives a five-star review from us \u2014 the sole exception being unreported damage to the property. We believe in fairness and transparency.",
    bridge: "Vacation rental ratings differ considerably from hotel conventions. The following illustrates what each rating conveys to Airbnb:",
    footer: "Should anything fall short, we would welcome the opportunity to attend to it.",
  },
  warm_local: {
    headline: "Our approach to reviews",
    lead: "We give every guest a five-star review \u2014 unless there\u2019s unreported damage to the place. Fair\u2019s fair, right?",
    bridge: "Vacation rental ratings work differently to hotels. Here\u2019s what each star actually means to Airbnb:",
    footer: "If anything\u2019s not right, just let us know \u2014 we\u2019re here to help.",
  },
  minimalist: {
    headline: "Our approach to reviews",
    lead: "We leave every guest a five-star review, unless there are unreported damages. Fair reviews, both ways.",
    bridge: "Vacation rental ratings differ from hotels:",
    footer: "Feedback welcome anytime.",
  },
}

// Booking.com: one-way reviews, 6-category hotel scale → educate guests
const BOOKING_VOICE: Record<HouseVoice, ReviewVoiceCopy> = {
  house_standard: {
    headline: "How Booking.com scores work",
    lead: "Booking.com\u2019s rating system was designed for hotels. Categories like \u201cFacilities\u201d and \u201cStaff\u201d don\u2019t always translate to a private home \u2014 and the 1\u201310 scale is stricter than it looks.",
    bridge: "Here\u2019s what each score range actually signals to the platform:",
    footer: "If anything could be better during your stay, we\u2019d love to hear from you first.",
  },
  attentive_host: {
    headline: "How Booking.com scores work",
    lead: "Booking.com\u2019s rating system was designed for hotels. Categories like \u201cFacilities\u201d and \u201cStaff\u201d don\u2019t always translate to a private home \u2014 and the 1\u201310 scale is stricter than it looks.",
    bridge: "Here\u2019s what each score range actually signals to the platform:",
    footer: "If anything could be better, please let us know \u2014 we\u2019re always happy to help.",
  },
  english_butler: {
    headline: "How Booking.com scores work",
    lead: "Booking.com\u2019s rating system was conceived for hotels. Categories such as \u201cFacilities\u201d and \u201cStaff\u201d do not map neatly onto a private residence \u2014 and the 1\u201310 scale is far more exacting than it appears.",
    bridge: "The following illustrates what each score range conveys to the platform:",
    footer: "Should anything fall short, we would welcome the opportunity to attend to it.",
  },
  warm_local: {
    headline: "How Booking.com scores work",
    lead: "Booking.com\u2019s rating system was built for hotels, not holiday homes. Categories like \u201cFacilities\u201d and \u201cStaff\u201d don\u2019t quite fit \u2014 and the scores are harsher than they look.",
    bridge: "Here\u2019s what the scores actually mean to the platform:",
    footer: "If anything\u2019s not right, just let us know \u2014 we\u2019re here to help.",
  },
  minimalist: {
    headline: "How Booking.com scores work",
    lead: "Booking.com rates on a hotel scale. Categories like \u201cFacilities\u201d weren\u2019t made for homes. The 1\u201310 scale is stricter than it looks.",
    bridge: "What each score range signals:",
    footer: "Feedback welcome anytime.",
  },
}

function getReviewVoice(platform: Platform, voice: HouseVoice | string | undefined | null): ReviewVoiceCopy {
  const key = ((voice || "house_standard") as string).toLowerCase().trim() as HouseVoice
  const voices = platform === "booking" ? BOOKING_VOICE : AIRBNB_VOICE
  return voices[key] || voices.house_standard
}

// ---------------------------------------------------------------------------
// Star SVG (Airbnb)
// ---------------------------------------------------------------------------

function Star({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="eink-star"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    >
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  )
}

function Stars({ count }: { count: number }) {
  return (
    <span className="eink-stars">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} filled={i < count} />
      ))}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Score badge (Booking.com)
// ---------------------------------------------------------------------------

function ScoreBadge({ label }: { label: string }) {
  return <span className="eink-score-badge">{label}</span>
}

// ---------------------------------------------------------------------------
// Rating table data — platform-specific
// ---------------------------------------------------------------------------

interface AirbnbRating { stars: number; guest: string; platform: string }
interface BookingRating { score: string; guest: string; platform: string }

const AIRBNB_RATINGS: AirbnbRating[] = [
  { stars: 5, guest: "I enjoyed my stay",       platform: "Acceptable" },
  { stars: 4, guest: "A few things to improve", platform: "Not good enough" },
  { stars: 3, guest: "It was okay",             platform: "Bad" },
  { stars: 2, guest: "Below expectations",       platform: "Terrible" },
  { stars: 1, guest: "A bad experience",         platform: "Unacceptable" },
]

const BOOKING_RATINGS: BookingRating[] = [
  { score: "10",    guest: "Exceptional stay",        platform: "Excellent" },
  { score: "8\u20139",  guest: "Very good, minor issues",  platform: "Good / expected" },
  { score: "7",     guest: "Good, some things to fix", platform: "Below average" },
  { score: "5\u20136",  guest: "Okay, not great",         platform: "Poor" },
  { score: "1\u20134",  guest: "A bad experience",         platform: "Unacceptable" },
]

// ---------------------------------------------------------------------------
// Page — route: /p/:slug/review-guide?platform=airbnb|booking
// ---------------------------------------------------------------------------

export function ReviewGuidePage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const [data, setData] = useState<PublicGuidebookPayload | null>(null)
  const [loading, setLoading] = useState(true)

  const platform: Platform = (searchParams.get("platform")?.toLowerCase().trim() === "booking" ? "booking" : "airbnb")

  const normalizedSlug = useMemo(() => String(slug || "").trim().toLowerCase(), [slug])

  const loadData = useCallback(async () => {
    if (!normalizedSlug) { setLoading(false); return }
    try {
      const result = await fetchPublicGuidebookBySlug(normalizedSlug)
      setData(result)
    } catch {
      // Show guide anyway — property name/voice are nice-to-have
    } finally {
      setLoading(false)
    }
  }, [normalizedSlug])

  useEffect(() => { loadData() }, [loadData])

  const voice = useMemo(() => getReviewVoice(platform, data?.house_voice), [platform, data?.house_voice])
  const propertyName = data?.property?.name || ""

  if (loading) {
    return <div className="eink-frame"><div className="eink-loading">Loading&hellip;</div></div>
  }

  return (
    <div className="eink-frame">
      <div className="eink-card">
        {/* Property name */}
        {propertyName && <p className="eink-prop">{propertyName}</p>}

        {/* Headline */}
        <h1 className="eink-h1">{voice.headline}</h1>

        {/* Divider */}
        <hr className="eink-hr" />

        {/* Lead */}
        <p className="eink-sub">{voice.lead}</p>

        {/* Bridge */}
        <p className="eink-bridge">{voice.bridge}</p>

        {/* Rating table */}
        <div className="eink-table" role="table">
          <div className="eink-thead" role="row">
            <span className="eink-th eink-th--guest">What you mean</span>
            <span className="eink-th eink-th--stars">{platform === "booking" ? "Score" : "Rating"}</span>
            <span className="eink-th eink-th--platform">What the platform reads</span>
          </div>

          {platform === "booking"
            ? BOOKING_RATINGS.map((r) => (
                <div key={r.score} className="eink-tr" role="row">
                  <span className="eink-td eink-td--guest">{r.guest}</span>
                  <span className="eink-td eink-td--stars"><ScoreBadge label={r.score} /></span>
                  <span className="eink-td eink-td--platform">{r.platform}</span>
                </div>
              ))
            : AIRBNB_RATINGS.map((r) => (
                <div key={r.stars} className="eink-tr" role="row">
                  <span className="eink-td eink-td--guest">{r.guest}</span>
                  <span className="eink-td eink-td--stars"><Stars count={r.stars} /></span>
                  <span className="eink-td eink-td--platform">{r.platform}</span>
                </div>
              ))
          }
        </div>

        {/* Footer */}
        <hr className="eink-hr" />
        <p className="eink-footer">{voice.footer}</p>
      </div>
    </div>
  )
}
